import { FlexProps, UploadFileInput } from "@goodfill/ui-components";
import { useRef, useState, FocusEventHandler, Ref, forwardRef, useImperativeHandle, ChangeEvent } from "react";
import { toast } from "react-hot-toast";

type _CustomRefType = {
  focus: () => void;
};

export interface FileUploadProps {
  fileName: string;
  onUpload: (data: File) => Promise<void>;
  onReset: () => void;
  width?: FlexProps["width"];
  text?: string;
  onBlur: FocusEventHandler<HTMLInputElement>;
  ref?: Ref<_CustomRefType>;
  id?: string;
  disabled?: boolean;
}

const acceptedMimeTypes = ["image/jpeg"];

export const FileUpload = forwardRef<_CustomRefType, FileUploadProps>(
  ({ width, text, onUpload, onReset, onBlur, id, disabled }, ref) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [file, setFile] = useState(null as File | null);

    const resetFile = () => {
      setFile(null);
      onReset();
    };

    useImperativeHandle(ref, () => ({
      focus() {
        if (!fileRef.current) {
          return;
        }

        const current = fileRef.current;
        current.focus();
      },
    }));

    const handleOnUpload = async (file: File) => {
      setIsUploading(true);
      try {
        await onUpload(file);
      } catch (e) {
        console.error(e);
        toast.error(`File upload failed: ${e}`);
      } finally {
        setIsUploading(false);
      }
    };

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const _file = e.currentTarget.files?.[0];

      if (_file != null) {
        setFile(_file);
        await handleOnUpload(_file);
      }
    };

    return (
      <div className={`flex flex-col ${width} w-1/2`}>
        <UploadFileInput
          id={id}
          accept={acceptedMimeTypes}
          file={file}
          fileRef={fileRef}
          isUploading={isUploading}
          onFileChange={onFileChange}
          resetFile={resetFile}
          width={width}
          text={text}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
    );
  },
);
